"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export interface SlotReservation {
  id: string;
  expiresAt: string; // ISO
  date: string; // original date param we reserved for
  startTime: string;
  type?: string;
}

interface HookReturn {
  reservation: SlotReservation | null;
  reserving: boolean;
  /** Reserve a slot. Returns the reservation or throws on failure. */
  reserve: (params: { date: string; startTime: string; type?: string }) => Promise<SlotReservation | null>;
  /** Release the current reservation (e.g. on back nav). */
  release: () => Promise<void>;
  /** Re-reserve the currently-held slot (after expiry). */
  retry: () => Promise<boolean>;
  /** Whether the current reservation has expired (client-side countdown). */
  expired: boolean;
  /** Called by the banner when countdown hits zero. Flips `expired` to true. */
  markExpired: () => void;
  /** Reset the expired state — used when the customer picks a new time. */
  clearExpired: () => void;
}

const STORAGE_KEY = "wig-slot-reservation";

/**
 * React hook that owns an appointment slot reservation lifecycle —
 * create on time-selection, poll countdown, surface expiry to the
 * caller so they can show the modal, retry or pick-new from the
 * modal. Survives page reloads via sessionStorage.
 */
export function useSlotReservation(): HookReturn {
  const [reservation, setReservation] = useState<SlotReservation | null>(null);
  const [reserving, setReserving] = useState(false);
  const [expired, setExpired] = useState(false);
  // Track the params of the currently-held slot so retry() can re-
  // reserve without the caller keeping state.
  const paramsRef = useRef<{ date: string; startTime: string; type?: string } | null>(null);

  // Hydrate from sessionStorage once on mount. The reservation survives
  // page refreshes as long as the id is still active server-side.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as SlotReservation;
      // Verify with the server — a refresh could be past the expiry.
      fetch(`/api/appointments/reserve-slot/${parsed.id}`)
        .then((r) => r.json())
        .then((data) => {
          if (data.active) {
            setReservation(parsed);
            paramsRef.current = { date: parsed.date, startTime: parsed.startTime, type: parsed.type };
          } else {
            sessionStorage.removeItem(STORAGE_KEY);
          }
        })
        .catch(() => sessionStorage.removeItem(STORAGE_KEY));
    } catch {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const persist = (r: SlotReservation | null) => {
    if (typeof window === "undefined") return;
    if (r) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(r));
    else sessionStorage.removeItem(STORAGE_KEY);
  };

  const reserve = useCallback(
    async (params: { date: string; startTime: string; type?: string }): Promise<SlotReservation | null> => {
      setReserving(true);
      setExpired(false);
      try {
        // Release any prior hold first so we don't leak reservations
        // if the customer flipped between times.
        if (reservation) {
          fetch(`/api/appointments/reserve-slot/${reservation.id}`, { method: "DELETE" }).catch(() => {});
        }
        const res = await fetch("/api/appointments/reserve-slot", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(params),
        });
        const data = await res.json();
        if (!res.ok) {
          // 409 = slot_full; caller can decide what to show. Throw
          // the server's message so the caller's toast has context.
          throw new Error(data.message || data.error || "Failed to reserve slot");
        }
        // Admin disabled slot reservations entirely — treat as a no-op.
        // `reservation` stays null so the banner + expiry modal stay
        // hidden. Appointment POST will omit reservationId and the
        // server accepts the booking without the hold.
        if (data.disabled) {
          setReservation(null);
          paramsRef.current = null;
          persist(null);
          return null;
        }
        const next: SlotReservation = {
          id: data.id,
          expiresAt: data.expiresAt,
          date: params.date,
          startTime: params.startTime,
          type: params.type,
        };
        setReservation(next);
        paramsRef.current = params;
        persist(next);
        return next;
      } finally {
        setReserving(false);
      }
    },
    [reservation]
  );

  const release = useCallback(async () => {
    if (!reservation) return;
    try {
      await fetch(`/api/appointments/reserve-slot/${reservation.id}`, { method: "DELETE" });
    } catch {
      // non-fatal — the TTL will handle it
    }
    setReservation(null);
    paramsRef.current = null;
    persist(null);
  }, [reservation]);

  const retry = useCallback(async (): Promise<boolean> => {
    if (!paramsRef.current) return false;
    try {
      await reserve(paramsRef.current);
      return true;
    } catch {
      return false;
    }
  }, [reserve]);

  const markExpired = useCallback(() => setExpired(true), []);
  const clearExpired = useCallback(() => {
    setExpired(false);
    setReservation(null);
    paramsRef.current = null;
    persist(null);
  }, []);

  // Release on navigation away. Not 100% reliable (mobile Safari
  // drops beforeunload) but helps free slots in the common case.
  useEffect(() => {
    if (!reservation) return;
    const onUnload = () => {
      try {
        navigator.sendBeacon(
          `/api/appointments/reserve-slot/${reservation.id}`,
          JSON.stringify({ _method: "DELETE" })
        );
      } catch {
        // sendBeacon doesn't support DELETE directly on all browsers; swallow
      }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [reservation]);

  return { reservation, reserving, reserve, release, retry, expired, markExpired, clearExpired };
}
